import {
  hasDicomStoreQueryParam,
  getGcpDicomStorePathFromDicomwebRoot,
  getDicomStorePathFromSearch,
  setDicomStoreInBrowserUrl,
} from './dicomStoreUrl';

const GCP_HEALTHCARE_API_BASE = 'https://healthcare.googleapis.com/v1/';

export type StudyDicomStoreResolution =
  | { status: 'found'; storePath: string; storeName: string; dicomwebRoot: string }
  | { status: 'store-denied'; storeName: string; storePath?: string }
  | { status: 'not-found' }
  | { status: 'no-access' }
  | { status: 'skipped' }
  | { status: 'unavailable' };

type DatasetContext = {
  projectId: string;
  location: string;
  datasetId: string;
};

type ResolveScope = {
  projectId: string;
  location?: string;
};

function getParamCaseInsensitive(sp: URLSearchParams, key: string): string | null {
  if (sp.has(key)) {
    return sp.get(key);
  }
  const target = key.toLowerCase();
  for (const [k, v] of sp.entries()) {
    if (k.toLowerCase() === target) {
      return v;
    }
  }
  return null;
}

function getDicomStoreNameFromDicomwebRoot(dicomwebRoot?: string): string | null {
  const match = String(dicomwebRoot || '').match(/\/dicomStores\/([^/]+)\/dicomWeb\/?$/i);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function getDicomStoreNameFromStorePath(storePath?: string): string | null {
  if (!storePath) {
    return null;
  }
  const match = String(storePath).match(/\/dicomStores\/([^/]+)\/?$/i);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function parseDatasetContextFromDicomwebRoot(url?: string): DatasetContext | null {
  const match = String(url || '').match(/projects\/([^/]+)\/locations\/([^/]+)\/datasets\/([^/]+)/i);
  if (!match) {
    return null;
  }
  const [, projectId, location, datasetId] = match;
  return projectId && location && datasetId ? { projectId, location, datasetId } : null;
}

function buildDatasetBasePath(datasetContext: DatasetContext): string {
  const { projectId, location, datasetId } = datasetContext;
  return `projects/${projectId}/locations/${location}/datasets/${datasetId}`;
}

function buildStoreDicomwebRoot(datasetContext: DatasetContext, storeName: string): string {
  const base = GCP_HEALTHCARE_API_BASE.replace(/\/+$/, '/');
  return `${base}${buildDatasetBasePath(datasetContext)}/dicomStores/${encodeURIComponent(
    storeName
  )}/dicomWeb`;
}

function buildGcpDicomwebRootFromStorePath(storePath: string): string {
  const trimmed = String(storePath || '').trim().replace(/^\//, '');
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, '');
  }
  const base = GCP_HEALTHCARE_API_BASE.replace(/\/+$/, '/');
  const path = trimmed.replace(/^v1\//, '');
  if (path.toLowerCase().endsWith('/dicomweb')) {
    return `${base}${path}`;
  }
  return `${base}${path}/dicomWeb`;
}

function getDicomStorePermissionApiUrl(extensionManager, dataSourceName: string): string | undefined {
  const dataSourceDefinition = extensionManager.getDataSourceDefinition(dataSourceName);
  const fromDataSourceConfig = dataSourceDefinition?.configuration?.dicomStorePermissionApiUrl;
  if (fromDataSourceConfig) {
    return fromDataSourceConfig;
  }
  return (window as { config?: { dicomStorePermissionApiUrl?: string } })?.config
    ?.dicomStorePermissionApiUrl;
}

function getDicomStudyResolveApiUrl(extensionManager, dataSourceName: string): string | undefined {
  const dataSourceDefinition = extensionManager.getDataSourceDefinition(dataSourceName);
  const fromDataSourceConfig = dataSourceDefinition?.configuration?.dicomStudyResolveApiUrl;
  const configuredUrl =
    fromDataSourceConfig ||
    (window as { config?: { dicomStudyResolveApiUrl?: string } })?.config?.dicomStudyResolveApiUrl;
  const trimmed = String(configuredUrl || '').trim();
  return trimmed || undefined;
}

function toRequestUrl(configuredUrl: string): URL {
  const trimmed = configuredUrl.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return new URL(trimmed);
  }

  const baseOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost';
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return new URL(path, baseOrigin);
}

function getConfiguredResolveScope(
  extensionManager,
  dataSourceName: string
): ResolveScope | null {
  const dataSourceDefinition = extensionManager.getDataSourceDefinition(dataSourceName);
  const config = dataSourceDefinition?.configuration || {};
  const globalConfig = (window as {
    config?: { gcpProjectId?: string; gcpLocation?: string };
  })?.config;

  const projectId = String(config.gcpProjectId || globalConfig?.gcpProjectId || '').trim();
  const location = String(config.gcpLocation || globalConfig?.gcpLocation || '').trim();

  if (!projectId) {
    return null;
  }

  return {
    projectId,
    location: location || undefined,
  };
}

type ResolveStudyApiResponse = {
  status?: string;
  storePath?: string;
  storeName?: string;
  dicomwebRoot?: string;
};

async function resolveStudyViaApi({
  resolveApiUrl,
  projectId,
  location,
  studyInstanceUIDs,
  authHeaders,
}: {
  resolveApiUrl: string;
  projectId: string;
  location?: string | null;
  studyInstanceUIDs: string[];
  authHeaders: Record<string, string>;
}): Promise<StudyDicomStoreResolution> {
  const url = toRequestUrl(resolveApiUrl);
  url.searchParams.set('projectId', projectId);
  if (location) {
    url.searchParams.set('location', location);
  }
  if (studyInstanceUIDs.length === 1) {
    url.searchParams.set('StudyInstanceUID', studyInstanceUIDs[0]);
  } else {
    url.searchParams.set('StudyInstanceUIDs', studyInstanceUIDs.join(','));
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: authHeaders,
  });
  const data = (await response.json().catch(() => null)) as ResolveStudyApiResponse | null;

  if (response.status === 200 && data?.status === 'found' && data.storePath) {
    const storeName =
      data.storeName?.trim() || getDicomStoreNameFromStorePath(data.storePath) || '';
    return {
      status: 'found',
      storePath: data.storePath,
      storeName,
      dicomwebRoot: data.dicomwebRoot || buildGcpDicomwebRootFromStorePath(data.storePath),
    };
  }

  if (response.status === 404 || data?.status === 'not-found') {
    return { status: 'not-found' };
  }

  if (response.status === 401 || response.status === 403) {
    return { status: 'no-access' };
  }

  throw new Error(
    (data as { error?: string })?.error ||
      `Error resolving study DICOM store: ${response.status} - ${response.statusText}`
  );
}

function buildPermissionApiUrl(
  dicomStorePermissionApiUrl: string,
  datasetContext: DatasetContext | null
): string {
  if (!datasetContext) {
    return dicomStorePermissionApiUrl;
  }

  try {
    const url = toRequestUrl(dicomStorePermissionApiUrl);
    if (!url.searchParams.get('datasetId')) {
      url.searchParams.set('datasetId', datasetContext.datasetId);
    }
    if (!url.searchParams.get('projectId')) {
      url.searchParams.set('projectId', datasetContext.projectId);
    }
    if (!url.searchParams.get('location')) {
      url.searchParams.set('location', datasetContext.location);
    }
    return url.toString();
  } catch {
    return dicomStorePermissionApiUrl;
  }
}

async function fetchPermittedStoreNames(
  dicomStorePermissionApiUrl: string,
  datasetContext: DatasetContext | null,
  authHeaders: Record<string, string>
): Promise<string[]> {
  const url = buildPermissionApiUrl(dicomStorePermissionApiUrl, datasetContext);
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders,
  });
  const data = await response.json().catch(() => null);

  if (response.status < 200 || response.status >= 300) {
    throw new Error(
      data?.error?.message ||
        `Error fetching DICOM store permissions: ${response.status} - ${response.statusText}`
    );
  }

  return Array.isArray(data?.storeNames) ? data.storeNames : [];
}

async function studyExistsInStore(
  qidoRoot: string,
  studyInstanceUID: string,
  authHeaders: Record<string, string>
): Promise<'found' | 'denied' | 'missing'> {
  const url = new URL(`${String(qidoRoot).replace(/\/$/, '')}/studies`);
  url.searchParams.append('StudyInstanceUID', studyInstanceUID);
  url.searchParams.append('limit', '1');

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      ...authHeaders,
      Accept: 'application/dicom+json',
    },
  });

  if (response.status === 401 || response.status === 403) {
    return 'denied';
  }

  if (!response.ok) {
    return 'missing';
  }

  const data = await response.json().catch(() => null);
  return Array.isArray(data) && data.length > 0 ? 'found' : 'missing';
}

async function allStudiesExistInStore(
  qidoRoot: string,
  studyInstanceUIDs: string[],
  authHeaders: Record<string, string>
): Promise<'found' | 'denied' | 'missing'> {
  for (const studyInstanceUID of studyInstanceUIDs) {
    const result = await studyExistsInStore(qidoRoot, studyInstanceUID, authHeaders);
    if (result !== 'found') {
      return result;
    }
  }
  return 'found';
}

function getRequestedStoreFromUrl(searchParams: URLSearchParams): {
  storeName: string | null;
  storePath: string | null;
} {
  const dicomwebRoot =
    getParamCaseInsensitive(searchParams, 'dicomwebRoot') ||
    getParamCaseInsensitive(searchParams, 'dicomWebRoot');
  const dicomStorePath = getDicomStorePathFromSearch(window.location.search, searchParams);

  const storeName = (
    getDicomStoreNameFromStorePath(dicomStorePath || '') ||
    getDicomStoreNameFromDicomwebRoot(dicomwebRoot || '')
  )?.trim() || null;
  const storePath = (
    dicomStorePath || getGcpDicomStorePathFromDicomwebRoot(dicomwebRoot || '') || null
  )?.trim() || null;

  return { storeName, storePath };
}

function parseDatasetContextFromStorePath(storePath?: string | null): DatasetContext | null {
  return parseDatasetContextFromDicomwebRoot(storePath || '');
}

function getDatasetContext(
  extensionManager,
  dataSourceName: string,
  searchParams?: URLSearchParams
): DatasetContext | null {
  const dicomStorePath = getDicomStorePathFromSearch(
    typeof window !== 'undefined' ? window.location.search : '',
    searchParams
  );
  const fromUrl = parseDatasetContextFromStorePath(dicomStorePath);
  if (fromUrl) {
    return fromUrl;
  }

  const dataSourceDefinition = extensionManager.getDataSourceDefinition(dataSourceName);
  const root =
    dataSourceDefinition?.configuration?.qidoRoot ||
    dataSourceDefinition?.configuration?.wadoRoot ||
    dataSourceDefinition?.configuration?.wadoUriRoot;
  return parseDatasetContextFromDicomwebRoot(root);
}

export function applyDicomStoreToSession({
  extensionManager,
  dataSourceName,
  storePath,
  persistDicomStoreSelection = false,
}: {
  extensionManager: AppTypes.ExtensionManager;
  dataSourceName: string;
  storePath: string;
  persistDicomStoreSelection?: boolean;
}): void {
  const dicomwebRoot = buildGcpDicomwebRootFromStorePath(storePath);
  const def = extensionManager.getDataSourceDefinition(dataSourceName);
  if (!def) {
    return;
  }

  extensionManager.updateDataSourceConfiguration(dataSourceName, {
    ...def.configuration,
    wadoUriRoot: dicomwebRoot,
    qidoRoot: dicomwebRoot,
    wadoRoot: dicomwebRoot,
  });

  if (persistDicomStoreSelection) {
    try {
      const storageKey = `ohif.dataSource.${dataSourceName}.config`;
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          wadoUriRoot: dicomwebRoot,
          qidoRoot: dicomwebRoot,
          wadoRoot: dicomwebRoot,
        })
      );
    } catch {
      // ignore
    }
  }

  setDicomStoreInBrowserUrl(storePath);
}

/**
 * Find which permitted DICOM store contains the requested studies.
 * When the URL names a store the user cannot access, returns store-denied with that store name.
 */
export async function resolveStudyDicomStore({
  studyInstanceUIDs,
  extensionManager,
  userAuthenticationService,
  dataSourceName,
  searchParams = new URLSearchParams(window?.location?.search || ''),
}: {
  studyInstanceUIDs: string[];
  extensionManager: AppTypes.ExtensionManager;
  userAuthenticationService: AppTypes.UserAuthenticationService;
  dataSourceName: string;
  searchParams?: URLSearchParams;
}): Promise<StudyDicomStoreResolution> {
  if (!studyInstanceUIDs?.length) {
    return { status: 'skipped' };
  }

  const explicitStoreInUrl = hasDicomStoreQueryParam(searchParams);
  const { storeName: requestedStoreName, storePath: requestedStorePath } =
    getRequestedStoreFromUrl(searchParams);

  const authHeaders = (userAuthenticationService.getAuthorizationHeader() || {}) as Record<
    string,
    string
  >;
  const datasetContext = getDatasetContext(extensionManager, dataSourceName, searchParams);
  const configuredResolveScope = getConfiguredResolveScope(extensionManager, dataSourceName);
  const permissionApiUrl = getDicomStorePermissionApiUrl(extensionManager, dataSourceName);
  const resolveApiUrl = getDicomStudyResolveApiUrl(extensionManager, dataSourceName);

  if (!explicitStoreInUrl && resolveApiUrl) {
    const resolveScope = configuredResolveScope || datasetContext;

    if (!resolveScope?.projectId) {
      return { status: 'unavailable' };
    }

    try {
      const resolved = await resolveStudyViaApi({
        resolveApiUrl,
        projectId: resolveScope.projectId,
        location: resolveScope.location,
        studyInstanceUIDs,
        authHeaders,
      });

      return resolved;
    } catch (error) {
      console.warn('Failed to resolve study via cross-dataset API:', error);
      return { status: 'unavailable' };
    }
  }

  let permittedStoreNames: string[] = [];

  if (permissionApiUrl) {
    try {
      permittedStoreNames = await fetchPermittedStoreNames(
        permissionApiUrl,
        datasetContext,
        authHeaders
      );
    } catch (error) {
      console.warn('Failed to fetch permitted DICOM stores:', error);
      return { status: 'unavailable' };
    }
  }

  const normalizedRequestedStoreName = requestedStoreName?.trim() || null;
  const normalizedPermittedStoreNames = permittedStoreNames.map(name => name.trim());

  if (
    explicitStoreInUrl &&
    normalizedRequestedStoreName &&
    normalizedPermittedStoreNames.length > 0 &&
    !normalizedPermittedStoreNames.includes(normalizedRequestedStoreName)
  ) {
    // The permission API may be scoped to the wrong dataset when the build-time
    // default differs from the store named in a shared link. Confirm real GCP access
    // before showing a permission error.
    const urlDatasetContext =
      parseDatasetContextFromStorePath(requestedStorePath) || datasetContext;

    if (urlDatasetContext) {
      const qidoRoot = buildStoreDicomwebRoot(urlDatasetContext, normalizedRequestedStoreName);
      const searchResult = await allStudiesExistInStore(
        qidoRoot,
        studyInstanceUIDs,
        authHeaders
      );

      if (searchResult === 'found') {
        const storePath =
          requestedStorePath ||
          `${buildDatasetBasePath(urlDatasetContext)}/dicomStores/${normalizedRequestedStoreName}`;
        return {
          status: 'found',
          storePath,
          storeName: normalizedRequestedStoreName,
          dicomwebRoot: qidoRoot,
        };
      }

      if (searchResult === 'denied') {
        return {
          status: 'store-denied',
          storeName: normalizedRequestedStoreName,
          storePath: requestedStorePath || undefined,
        };
      }

      if (searchResult === 'missing') {
        return { status: 'not-found' };
      }
    }

    return {
      status: 'store-denied',
      storeName: normalizedRequestedStoreName,
      storePath: requestedStorePath || undefined,
    };
  }

  // Bare shared study links depend on the permission API to tell us which stores
  // this user can search. An empty list means the user cannot access any store.
  if (!explicitStoreInUrl && permissionApiUrl && permittedStoreNames.length === 0) {
    return { status: 'no-access' };
  }

  if (!datasetContext) {
    return { status: 'unavailable' };
  }

  const storesToSearch =
    permittedStoreNames.length > 0
      ? permittedStoreNames
      : normalizedRequestedStoreName
        ? [normalizedRequestedStoreName]
        : [];

  if (!storesToSearch.length) {
    return { status: 'unavailable' };
  }

  for (const storeName of storesToSearch) {
    const qidoRoot = buildStoreDicomwebRoot(datasetContext, storeName);
    const searchResult = await allStudiesExistInStore(qidoRoot, studyInstanceUIDs, authHeaders);

    if (searchResult === 'found') {
      const storePath = `${buildDatasetBasePath(datasetContext)}/dicomStores/${storeName}`;
      return {
        status: 'found',
        storePath,
        storeName,
        dicomwebRoot: qidoRoot,
      };
    }
  }

  if (explicitStoreInUrl && normalizedRequestedStoreName) {
    return { status: 'not-found' };
  }

  return { status: 'not-found' };
}
